import React from 'react';
import { AlertCircle } from 'lucide-react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', {
      error,
      componentStack: errorInfo.componentStack,
      errorMessage: error.message,
      errorStack: error.stack
    });
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center mb-4 text-red-600">
              <AlertCircle className="h-6 w-6 mr-2" />
              <h2 className="text-xl font-semibold">Something went wrong</h2>
            </div>
            
            <div className="mb-4">
              <div className="text-sm text-gray-600 mb-2">Error details:</div>
              <pre className="text-sm bg-gray-100 p-4 rounded overflow-auto max-h-[200px] text-red-600">
                {this.state.error?.message}
              </pre>
            </div>
            
            {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
              <div className="mb-4">
                <div className="text-sm text-gray-600 mb-2">Component stack:</div>
                <pre className="text-sm bg-gray-100 p-4 rounded overflow-auto max-h-[200px] text-gray-700">
                  {this.state.errorInfo.componentStack}
                </pre>
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;